import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	MoreVertical,
	BotOff,
	Bot,
	SlidersHorizontal,
	FileDown,
	Clock,
} from "lucide-react";

import { Button } from "../ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "../ui/dropdown-menu";

import { i18n } from "../../translate/i18n";
import { toast } from "react-toastify";
import api from "../../services/api";
import ConfirmationModal from "../ConfirmationModal";
import TransferTicketModal from "../TransferTicketModal";
import TicketAutomationModal from "../TicketAutomationModal";
import toastError from "../../errors/toastError";
import { Can } from "../Can";
import { AuthContext } from "../../context/Auth/AuthContext";
import AdicionarAoKanbanModal from "../AdicionarAoKanbanModal";
import ExportarConversaModal from "../ExportarConversaModal";
import ScheduledMessageModal from "../ScheduledMessageModal";
import usePermissions from "../../hooks/usePermissions";

const TicketOptionsMenu = ({ ticket }) => {
	const [menuOpen, setMenuOpen] = useState(false);
	const [exportarAberto, setExportarAberto] = useState(false);
	const [funnelModalOpen, setFunnelModalOpen] = useState(false);
	const [confirmationOpen, setConfirmationOpen] = useState(false);
	const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
	// NOVO: modal de agendamento DIRETO do ticket (com dados pré-preenchidos).
	const [schedModalOpen, setSchedModalOpen] = useState(false);
	const isMounted = useRef(true);
	const { user } = useContext(AuthContext);
	const { temPermissao } = usePermissions();
	const navigate = useNavigate();

	useEffect(() => {
		return () => {
			isMounted.current = false;
		};
	}, []);

	const handleDeleteTicket = async () => {
		try {
			await api.delete(`/tickets/${ticket.id}`);
		} catch (err) {
			toastError(err);
		}
	};

	// Cala o robô só nesta conversa. Quem está atendendo às vezes precisa que
	// o fluxo, o bot da fila e a IA parem de responder por cima dele — antes
	// isso só era possível desligando na conexão, o que afeta todo mundo.
	const [pausado, setPausado] = useState(Boolean(ticket.botPaused));
	const [automacaoAberta, setAutomacaoAberta] = useState(false);

	// Este componente não desmonta ao trocar de atendimento — o pai só troca a
	// prop `ticket`. Sem sincronizar, o rótulo continuava mostrando o estado do
	// atendimento anterior até alguém recarregar a página.
	useEffect(() => {
		setPausado(Boolean(ticket.botPaused));
	}, [ticket.id, ticket.botPaused]);

	const alternarRobo = async () => {
		const novo = !pausado;

		setMenuOpen(false);

		try {
			await api.put(`/tickets/${ticket.id}/bot`, { paused: novo });
			setPausado(novo);
			toast.success(
				novo
					? "Robô pausado nesta conversa."
					: "Robô liberado para responder aqui."
			);
		} catch (err) {
			toastError(err);
		}
	};

	const handleOpenTransferModal = () => {
		setTransferTicketModalOpen(true);
		setMenuOpen(false);
	};

	const handleCloseTransferTicketModal = () => {
		if (isMounted.current) {
			setTransferTicketModalOpen(false);
		}
	};


	// Quanto falta da pausa, para o aviso do cabeçalho. O servidor não avisa
	// quando o prazo vence — ele limpa na próxima mensagem —, então a conta é
	// feita aqui.
	const restanteDaPausa = (() => {
		if (!pausado || !ticket.botPausedUntil) return null;

		const minutos = Math.round(
			(new Date(ticket.botPausedUntil).getTime() - Date.now()) / 60000
		);

		if (minutos <= 0) return null;
		if (minutos < 60) return `${minutos}min`;

		return `${Math.floor(minutos / 60)}h`;
	})();

	return (
		<>
			{/* Robô pausado é estado do atendimento: tem de aparecer sem abrir
			    menu nenhum. Clicar leva direto para a automação. */}
			{pausado && (
				<Button
					variant="ghost"
					size="sm"
					onClick={() => setAutomacaoAberta(true)}
					title="Robô pausado nesta conversa — clique para ajustar"
					className="h-8 gap-1.5 rounded-full bg-amber-100 px-2.5 text-amber-700 hover:bg-amber-200 dark:bg-amber-500/15 dark:text-amber-300"
				>
					<BotOff className="h-4 w-4" />
					<span className="text-xs font-medium">
						{restanteDaPausa || "pausado"}
					</span>
				</Button>
			)}
			<ExportarConversaModal
				aberto={exportarAberto}
				aoFechar={() => setExportarAberto(false)}
				ticketId={ticket.id}
				contato={ticket.contact?.name}
			/>
			<AdicionarAoKanbanModal
				open={funnelModalOpen}
				onClose={() => setFunnelModalOpen(false)}
				contact={ticket.contact}
			/>
			<DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
				<DropdownMenuTrigger asChild>
					<Button variant="ghost" size="icon">
						<MoreVertical className="h-4 w-4" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end">
					<DropdownMenuItem onClick={alternarRobo}>
						{pausado ? (
							<Bot className="h-4 w-4" />
						) : (
							<BotOff className="h-4 w-4" />
						)}
						{pausado ? "Reativar robô" : "Pausar robô (fluxo e IA)"}
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => {
							setMenuOpen(false);
							setAutomacaoAberta(true);
						}}
					>
						<SlidersHorizontal className="h-4 w-4" />
						Automação da conversa…
					</DropdownMenuItem>
					<DropdownMenuItem
						onClick={() => {
							setMenuOpen(false);
							setExportarAberto(true);
						}}
					>
						<FileDown className="h-4 w-4" />
						Exportar conversa (PDF)…
					</DropdownMenuItem>
					{/* NOVO: atalho para agendar mensagem diretamente deste ticket.
					    Abre o modal com destinatário e conexão JÁ preenchidos e
					    BLOQUEADOS — evita erro humano de agendar para cliente errado. */}
					<DropdownMenuItem
						onClick={() => {
							setMenuOpen(false);
							setSchedModalOpen(true);
						}}
					>
						<Clock className="h-4 w-4" />
						Agendar mensagem para este contato…
					</DropdownMenuItem>
					{/* Também em "aguardando": transferir é o único caminho para pôr
					    uma conversa numa FILA, e o momento de encaminhar um contato
					    novo para o setor certo é justamente antes de alguém aceitar.
					    Exigir "aberta" obrigava a assumir o atendimento só para poder
					    passá-lo adiante — e, na prática, quem chegava pela conversa
					    concluía que o sistema não tinha como enfileirar.
					    Encerrada continua fora: ali não há o que encaminhar. */}
					{(ticket.status === "open" || ticket.status === "pending") && (
						<DropdownMenuItem onClick={handleOpenTransferModal}>
							{i18n.t("ticketOptionsMenu.transfer")}
						</DropdownMenuItem>
					)}
					{/* Grupo não vira oportunidade: o negócio é com uma pessoa, e
					    o contato de um grupo é o grupo inteiro. */}
					{!ticket.isGroup && (
						<DropdownMenuItem
							onClick={() => {
								setMenuOpen(false);
								setFunnelModalOpen(true);
							}}
						>
							Adicionar ao Kanban
						</DropdownMenuItem>
					)}
					<Can
						role={user.profile}
						perform="ticket-options:replay"
						yes={() => (
							<DropdownMenuItem
								onClick={() => navigate(`/tickets/${ticket.id}/replay`)}
							>
								Ver replay
							</DropdownMenuItem>
						)}
					/>
					{(() => {
						const podeLegado = Can({
							role: user?.profile,
							perform: "ticket-options:deleteTicket",
							yes: () => true,
							no: () => false,
						});
						const podeApagar = temPermissao("tickets:delete") || !!podeLegado;
						if (!podeApagar) return null;
						return (
							<DropdownMenuItem
								className="text-destructive focus:text-destructive"
								onClick={() => {
									setConfirmationOpen(true);
									setMenuOpen(false);
								}}
							>
								{i18n.t("ticketOptionsMenu.delete")}
							</DropdownMenuItem>
						);
					})()}
				</DropdownMenuContent>
			</DropdownMenu>
			<ConfirmationModal
				title={`${i18n.t("ticketOptionsMenu.confirmationModal.title")}${
					ticket.id
				} ${i18n.t("ticketOptionsMenu.confirmationModal.titleFrom")} ${
					// Sem o `?.` a tela inteira fica BRANCA quando o atendimento
					// não carrega — e ele não carrega sempre: a API responde 403
					// quando o ticket é movido para uma fila que a pessoa não vê,
					// e aí `ticket` fica {}. O título do modal derrubava o painel
					// por causa de um nome que nem estava à vista.
					ticket?.contact?.name || ""
				}?`}
				open={confirmationOpen}
				onClose={setConfirmationOpen}
				onConfirm={handleDeleteTicket}
			>
				{i18n.t("ticketOptionsMenu.confirmationModal.message")}
			</ConfirmationModal>
			<TransferTicketModal
				modalOpen={transferTicketModalOpen}
				onClose={handleCloseTransferTicketModal}
				ticketid={ticket.id}
				ticketWhatsappId={ticket.whatsappId}
			/>
			<TicketAutomationModal
				open={automacaoAberta}
				onClose={() => setAutomacaoAberta(false)}
				ticket={ticket}
				onSaved={(atualizado) => setPausado(Boolean(atualizado?.botPaused))}
			/>
			{/* NOVO: modal de agendamento VINCULADO a este ticket.
			    lockedIds=true garante que ninguém troque destinatário/conexão por engano.
			    Apenas envia ticketId no payload se ticket.id existir (proteção NPE). */}
			<ScheduledMessageModal
				open={schedModalOpen}
				onClose={() => {
					if (isMounted.current) setSchedModalOpen(false);
				}}
				initialTicket={ticket?.id ? ticket : undefined}
				initialContact={ticket?.contact}
				lockedIds={Boolean(ticket?.id)}
				onSaved={() =>
					toast.success("Agendamento criado para este ticket!")
				}
			/>
		</>
	);
};

export default TicketOptionsMenu;
